import './requestCard.scss';

import { Mods } from '@approvalmax/theme';
import { openSplitViewState } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { useAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { FC, memo, useEffect, useMemo, useRef } from 'react';
import bemFactory from 'react-bem-factory';

import { DearRequestCard } from '../../containers/dear/DearRequestCard/DearRequestCard';
import { NetSuiteRequestCard } from '../../containers/netsuite/NetSuiteRequestCard/NetSuiteRequestCard';
import { QBooksRequestCard } from '../../containers/qbooks/QBooksRequestCard/QBooksRequestCard';
import StandaloneRequestCard from '../../containers/standalone/StandaloneRequestCard';
import { XeroRequestCard } from '../../containers/xero/XeroRequestCard/XeroRequestCard';
import { AddAsWatchersPopup } from '../AddAsWatchersPopup/AddAsWatchersPopup';
import { messages } from './RequestCard.messages';

const bem = bemFactory.block('reql-request-card');

interface RequestCardProps extends Mods<'readOnly'> {
    className?: string;
    setContainerTitle?: (title: string) => void;
    request: selectors.types.ExpandedRequest | null;
}

export const RequestCard: FC<RequestCardProps> = memo((props) => {
    const { className, setContainerTitle, request, readOnly } = props;
    const renderError = false; // TODO: routing - handle error and show error card

    const rootRef = useRef<HTMLDivElement>(null);
    const [openSplitView, setOpenSplitView] = useAtom(openSplitViewState);

    useEffect(() => {
        if (rootRef?.current) {
            rootRef.current.scrollTop = 0;
        }
    }, [request?.id]);

    useEffect(() => {
        if (setContainerTitle && request?.displayName) {
            setContainerTitle(request.displayName);
        }
    }, [request, setContainerTitle]);

    const card = useMemo(() => {
        if (!request || renderError) return;

        switch (request.integrationType) {
            case domain.IntegrationType.Xero:
                return <XeroRequestCard className={bem('card')} request={request} readonly={readOnly} />;

            case domain.IntegrationType.QBooks:
                return <QBooksRequestCard className={bem('card')} request={request} readonly={readOnly} />;

            case domain.IntegrationType.NetSuite:
                return <NetSuiteRequestCard className={bem('card')} request={request} />;

            case domain.IntegrationType.Dear:
                return <DearRequestCard className={bem('card')} request={request} />;

            case domain.IntegrationType.None:
                return <StandaloneRequestCard className={bem('card')} request={request} />;

            default:
                throw errorHelpers.notSupportedError();
        }
    }, [readOnly, renderError, request]);

    if (!request) {
        openSplitView && setOpenSplitView(false);

        return null;
    }

    return (
        <div className={bem.add(className)()} ref={rootRef}>
            {renderError && (
                <div className={bem('render-error-card')}>
                    <div className={bem('render-error-content')}>
                        <div className={bem('render-error-icon')}>🚧</div>

                        <div className={bem('render-error-title')}>{messages.renderErrorTitle({ br: <br /> })}</div>

                        <div className={bem('render-error-description')}>
                            {messages.renderErrorDescription({
                                supportLink: (chunks: any) => <a href='mailto:support@approvalmax.com'>{chunks}</a>,
                            })}
                        </div>
                    </div>
                </div>
            )}

            {card}

            <AddAsWatchersPopup request={request} />
        </div>
    );
});

RequestCard.displayName = 'RequestCard';
